import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';

import { SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { writeDynamicDashboardCache } from '../shared/self-service-storage.utils';
import { DvmLandingPageComponent } from './dvm-landing-page.component';
import {
  BUILT_IN_LANDING_TEMPLATES,
  LANDING_PAGE_DASHBOARD_TYPE,
  LandingModule,
  LandingModuleLink,
  LandingPageConfig,
  LandingPageTemplate,
  extractLandingConfig,
  isLandingPageLayout,
  landingSlugify,
} from './landing-page-types';

/** A published dashboard the module buttons can point at. */
interface DashboardTargetOption {
  label: string;
  slug: string;
}

/**
 * Landing Page Template builder (`/dvm-landing-builder`).
 *
 * Step 1 — pick a predefined template (built-ins + whatever the deployment adds
 * in `assets/landing-page-templates/templates.json`; asset entries with the
 * same `id` override built-ins, so templates are configurable without code).
 *
 * Step 2 — edit every text/config of the page in a form beside a live preview:
 * header, section label, accent, and per-module badge/icon/title/description/
 * accent plus each button's label + target (published dashboard, app route, or
 * external URL). Modules and buttons can be added, removed and reordered.
 *
 * Publish reuses the SAME `dashboard_layouts` pipeline as the layout builder
 * (`publishLayoutToApi`, `dvm:dashboard-published` event, dynamic cache warm),
 * so the saved page appears in the dashboards menu and serves from
 * `/dvm-published/<slug>` — which also makes it usable as the environment's
 * `LANDING_ROUTE`.
 */
@Component({
  selector: 'app-dvm-landing-page-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, DvmLandingPageComponent],
  templateUrl: './dvm-landing-page-builder.component.html',
  styleUrl: './dvm-landing-page-builder.component.scss',
})
export class DvmLandingPageBuilderComponent implements OnInit {
  private static readonly TEMPLATES_ASSET_URL = 'assets/landing-page-templates/templates.json';

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly keycloak = inject(KeycloakService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly ui = inject(SelfServiceUiService);

  step: 'templates' | 'edit' = 'templates';
  templates: LandingPageTemplate[] = [...BUILT_IN_LANDING_TEMPLATES];
  config: LandingPageConfig | null = null;
  pageName = '';
  isPublishing = false;
  isLoadingExisting = false;
  /** Index of the module the form scrolls to when the preview is clicked. */
  activeModuleIndex = 0;
  /** Published dashboards the link buttons can target. */
  dashboardOptions: DashboardTargetOption[] = [];

  /** The already-published dashboard_id when editing an existing landing page —
   *  republish must UPDATE that row, never mint a new slug (mirrors the layout
   *  builder's restoredDashboardId). */
  private restoredDashboardId = '';

  ngOnInit(): void {
    this.loadTemplates();
    void this.loadDashboardTargets();
    const editSlug = (this.route.snapshot.queryParamMap.get('dashboard') || '').trim();
    if (editSlug) void this.loadExisting(editSlug);
  }

  // ── Step 1: templates ────────────────────────────────────────────────────

  private loadTemplates(): void {
    this.http.get<LandingPageTemplate[]>(DvmLandingPageBuilderComponent.TEMPLATES_ASSET_URL).subscribe({
      next: (list) => {
        const fromAsset = (Array.isArray(list) ? list : []).filter((t) => t && t.id && t.config);
        // Asset templates first (deployment-specific), built-ins after — deduped
        // by id so an asset entry can override a built-in outright.
        const seen = new Set(fromAsset.map((t) => t.id));
        this.templates = [...fromAsset, ...BUILT_IN_LANDING_TEMPLATES.filter((t) => !seen.has(t.id))];
      },
      error: () => {
        this.templates = [...BUILT_IN_LANDING_TEMPLATES];
      },
    });
  }

  useTemplate(template: LandingPageTemplate): void {
    // Deep-clone so edits never mutate the shared template object.
    this.config = JSON.parse(JSON.stringify(template.config)) as LandingPageConfig;
    if (!this.pageName.trim()) this.pageName = template.config.header?.title || template.name;
    this.activeModuleIndex = 0;
    this.step = 'edit';
  }

  backToTemplates(): void {
    this.step = 'templates';
  }

  backToLayoutBuilder(): void {
    void this.router.navigate(['/dvm-layout-builder']);
  }

  // ── Edit an existing published landing page (?dashboard=<slug>) ─────────

  private async loadExisting(slug: string): Promise<void> {
    this.isLoadingExisting = true;
    try {
      const email = await this.resolveCurrentEmail();
      const layoutJson = await firstValueFrom(this.dashboardService.getPublishedLayoutBySlug(slug, email));
      if (!layoutJson || !isLandingPageLayout(layoutJson)) {
        this.ui.showError(`No published landing page found for "${slug}".`);
        return;
      }
      this.config = extractLandingConfig(layoutJson);
      this.pageName = String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? slug);
      this.restoredDashboardId = String(layoutJson['dashboardId'] ?? '') || slug;
      this.step = 'edit';
    } catch {
      this.ui.showError('Could not load the landing page for editing.');
    } finally {
      this.isLoadingExisting = false;
    }
  }

  // ── Form edits ───────────────────────────────────────────────────────────

  onPreviewModuleSelect(index: number): void {
    this.activeModuleIndex = index;
    const el = document.getElementById(`landing-module-form-${index}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  addModule(): void {
    if (!this.config) return;
    const n = this.config.modules.length + 1;
    this.config.modules.push({
      id: this.newId('module'),
      badge: `MODULE ${String(n).padStart(2, '0')}`,
      icon: 'dashboard',
      title: 'New module',
      description: '',
      links: [{ id: this.newId('link'), label: 'Open', targetType: 'dashboard', target: '', style: 'outline' }],
    });
    this.activeModuleIndex = this.config.modules.length - 1;
  }

  removeModule(index: number): void {
    this.config?.modules.splice(index, 1);
    this.activeModuleIndex = Math.max(0, Math.min(this.activeModuleIndex, (this.config?.modules.length ?? 1) - 1));
  }

  moveModule(index: number, delta: number): void {
    const modules = this.config?.modules;
    if (!modules) return;
    const next = index + delta;
    if (next < 0 || next >= modules.length) return;
    [modules[index], modules[next]] = [modules[next], modules[index]];
    this.activeModuleIndex = next;
  }

  addLink(module: LandingModule): void {
    module.links.push({ id: this.newId('link'), label: 'Open', targetType: 'dashboard', target: '', style: 'outline' });
  }

  removeLink(module: LandingModule, index: number): void {
    module.links.splice(index, 1);
  }

  /** Turn a button into a sub-menu group (or grow its existing sub-menu) by
   *  adding a child entry. A button with children becomes an expand/collapse
   *  toggle in the rendered page; its own target is ignored. One level only. */
  addChildLink(link: LandingModuleLink): void {
    link.children = link.children || [];
    link.children.push({ id: this.newId('link'), label: 'Sub item', targetType: 'dashboard', target: '', style: 'outline' });
  }

  removeChildLink(link: LandingModuleLink, index: number): void {
    link.children?.splice(index, 1);
    // No children left → back to a plain navigating button.
    if (link.children && !link.children.length) delete link.children;
  }

  /** Placeholder for the target input, matched to the selected target type. */
  targetPlaceholder(link: LandingModuleLink): string {
    if (link.targetType === 'url') return 'https://example.com';
    if (link.targetType === 'route') return 'e.g. dvm-charts or generate-visualization';
    return 'published dashboard slug';
  }

  private newId(prefix: string): string {
    return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }

  // ── Publish ──────────────────────────────────────────────────────────────

  get canPublish(): boolean {
    return !!this.config && !!this.pageName.trim() && this.config.modules.length > 0 && !this.isPublishing;
  }

  async publish(): Promise<void> {
    if (!this.config) return;
    const name = this.pageName.trim();
    if (!name) {
      this.ui.showError('Name the landing page before publishing.');
      return;
    }
    if (!this.config.modules.length) {
      this.ui.showError('Add at least one module before publishing.');
      return;
    }

    this.isPublishing = true;
    try {
      const email = await this.resolveCurrentEmail();

      // Same duplicate-row guard the layout builder uses: if this email already
      // published a landing page under this display name, reuse its real
      // dashboard_id so publish UPDATES that row instead of inserting a fork.
      let dashboardId = this.restoredDashboardId;
      if (!dashboardId && email) {
        try {
          const owned = await firstValueFrom(this.dashboardService.fetchOwnedDashboardsFromApi(email));
          const normalized = name.toLowerCase();
          const match = owned.find((o) =>
            String((o.layoutJson as any)?.['dashboard-name'] ?? (o.layoutJson as any)?.['name'] ?? '').trim().toLowerCase() === normalized,
          );
          if (match) dashboardId = match.name;
        } catch {
          // Best-effort — fall through to minting a fresh id.
        }
      }
      if (!dashboardId) {
        dashboardId = `${landingSlugify(name) || 'landing-page'}-${Math.random().toString(36).slice(2, 8)}`;
      }
      this.restoredDashboardId = dashboardId;

      const layoutJson: Record<string, unknown> = {
        name,
        'dashboard-name': name,
        dashboardType: LANDING_PAGE_DASHBOARD_TYPE,
        landing: JSON.parse(JSON.stringify(this.config)),
        dashboardId,
        publishedAt: new Date().toISOString(),
      };

      await firstValueFrom(this.dashboardService.publishLayoutToApi(layoutJson, email, dashboardId));

      const slug = landingSlugify(dashboardId);
      // Warm the cache + notify the sidenav — same post-publish steps the
      // layout builder runs, so the page shows up in the menu at once and the
      // published view paints instantly on redirect.
      writeDynamicDashboardCache(slug, layoutJson);
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', { detail: { dashboardId, name } }));
      this.ui.showSuccess('Landing page published — opening it now.');
      void this.router.navigate(['/dvm-published', slug]);
    } catch (error) {
      console.error('[dvm-landing-builder] publish failed:', error);
      this.ui.showError('Unable to publish the landing page.');
    } finally {
      this.isPublishing = false;
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private async resolveCurrentEmail(): Promise<string> {
    try {
      if (await this.keycloak.isLoggedIn()) {
        return (await this.keycloak.loadUserProfile()).email || '';
      }
    } catch {
      // Not logged in / Keycloak unavailable — publish anonymously.
    }
    return '';
  }

  /** Published dashboards this user owns → options for the button target picker. */
  private async loadDashboardTargets(): Promise<void> {
    try {
      const email = await this.resolveCurrentEmail();
      if (!email) return;
      const owned = await firstValueFrom(this.dashboardService.fetchOwnedDashboardsFromApi(email));
      this.dashboardOptions = owned
        .map((o) => ({
          label: String((o.layoutJson as any)?.['dashboard-name'] ?? (o.layoutJson as any)?.['name'] ?? o.name).trim() || o.name,
          slug: o.slug,
        }))
        .filter((o) => !!o.slug)
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch {
      // No options — the slug can still be typed by hand.
    }
  }
}
